'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface LoginFormProps {
  onSwitchToSignUp: () => void;
}

export default function LoginForm({ onSwitchToSignUp }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    const supabase = createClient();

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        if (error.message.toLowerCase().includes('invalid') || error.message.toLowerCase().includes('credentials')) {
          setError('email', { message: 'Invalid email or password. Please try again.' });
        } else {
          toast.error(error.message);
        }
        setIsLoading(false);
        return;
      }

      toast.success('Signed in — welcome to Qelotio');
      router.push('/');
      router.refresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'An unexpected error occurred');
      setIsLoading(false);
    }
  };

  const fillCredentials = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  const demoCredentials = [
    { role: 'Platform Admin', email: 'admin@acmepayments.io', password: 'Qelotio@Demo1' },
  ];

  return (
    <div className="fade-in space-y-5">
      <div>
        <h2 className="text-[22px] font-bold text-foreground">Welcome back</h2>
        <p className="text-[13px] text-muted-foreground mt-1">Sign in to your Qelotio workspace</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div>
          <label htmlFor="login-email" className="block text-[12px] font-medium text-foreground mb-1.5">
            Work Email
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            suppressHydrationWarning
            {...register('email', {
              required: 'Email address is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
            className={`input-base w-full px-3 py-2.5 text-[13px] ${errors.email ? 'border-red-500/60' : ''}`}
            placeholder="you@company.com"
          />
          {errors.email && (
            <p className="mt-1.5 text-[11px] text-red-400 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} />
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="text-[12px] font-medium text-foreground">
              Password
            </label>
            <button
              type="button"
              suppressHydrationWarning
              className="text-[12px] text-primary hover:text-primary/80 transition-colors"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              suppressHydrationWarning
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
              className={`input-base w-full px-3 py-2.5 pr-10 text-[13px] ${errors.password ? 'border-red-500/60' : ''}`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              suppressHydrationWarning
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-[11px] text-red-400 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} />
              {errors.password.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            id="remember"
            type="checkbox"
            {...register('remember')}
            className="w-3.5 h-3.5 rounded border-border bg-input accent-primary cursor-pointer"
          />
          <label htmlFor="remember" className="text-[12px] text-muted-foreground cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          suppressHydrationWarning
          className="btn-primary w-full py-2.5 text-[14px] flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              <span>Signing in...</span>
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <div className="relative flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-[11px] text-muted-foreground flex-shrink-0">Demo account</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <div className="bg-muted/30 border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <Icon name="BeakerIcon" size={13} className="text-yellow-400" />
          <p className="text-[11px] font-semibold text-yellow-400">Demo credentials — click to autofill</p>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-border/50">
              <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Role</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Email</th>
              <th className="px-4 py-2 w-14" />
            </tr>
          </thead>
          <tbody>
            {demoCredentials.map((cred) => (
              <tr
                key={`demo-cred-${cred.role}`}
                className="border-b border-border/30 last:border-0 hover:bg-muted/40 cursor-pointer transition-colors"
                onClick={() => fillCredentials(cred.email, cred.password)}
              >
                <td className="px-4 py-2">
                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                    {cred.role}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span className="font-mono text-[11px] text-muted-foreground">{cred.email}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <span className="text-[10px] font-medium text-primary hover:text-primary/80">Use →</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-center text-[12px] text-muted-foreground">
        Don&apos;t have an account?{' '}
        <button suppressHydrationWarning onClick={onSwitchToSignUp} className="text-primary hover:text-primary/80 font-medium transition-colors">
          Create one
        </button>
      </p>
    </div>
  );
}